import type { AgentRecommendation } from "@productlane/zero/schema"

import type { AgentItemType } from "./format"
import { agentPath } from "./agent-route"
import { acceptedRowMeta, docsSourceLabel, rowMeta } from "./format"

export type AgentListTab = "open" | "accepted" | "declined"

export const AGENT_LIST_TABS: { id: AgentListTab; label: string }[] = [
	{ id: "open", label: "Open" },
	{ id: "accepted", label: "Accepted" },
	{ id: "declined", label: "Declined" },
]

export interface DecidedRow {
	id: string
	title: string
	type: AgentItemType
	meta: string
	at: number
	// Null where there is nothing left to open, such as a declined docs change.
	href: string | null
	reason: string | null
}

export interface ClosedGroup {
	id: string
	title?: string | null
	source: string
	closedAt?: number | null
	createdAt: number
	proposals?: readonly { status: string; articleId?: string | null }[]
}

/** A group can read ACCEPTED after a decline, so its proposals decide. */
export function closedGroupOutcome(
	group: Pick<ClosedGroup, "proposals">,
): Exclude<AgentListTab, "open"> {
	return (group.proposals ?? []).some((p) => p.status === "ACCEPTED")
		? "accepted"
		: "declined"
}

export function decidedRows({
	tab,
	recommendations,
	otherRecommendations,
	openGroupOwners,
	groups,
}: {
	tab: Exclude<AgentListTab, "open">
	recommendations: readonly AgentRecommendation[]
	// The other tab's cards, so a docs change behind any card shows once, as the card.
	otherRecommendations: readonly AgentRecommendation[]
	openGroupOwners: ReadonlySet<string>
	groups: readonly ClosedGroup[]
}): DecidedRow[] {
	const owned = new Set(openGroupOwners)
	for (const r of [...recommendations, ...otherRecommendations]) {
		if (r.proposalGroupId) owned.add(r.proposalGroupId)
	}
	const cards = recommendations.map((r): DecidedRow => ({
		id: r.id,
		title: r.title,
		type: r.type,
		meta:
			tab === "accepted"
				? acceptedRowMeta(
						r.type,
						r.conversationCount,
						r.payload?.measured?.resolved,
					)
				: rowMeta(r.type, r.conversationCount),
		at: r.updatedAt,
		href: agentPath("all", r.id),
		reason: tab === "declined" ? (r.dismissReason ?? null) : null,
	}))
	const docs = groups
		.filter((g) => !owned.has(g.id) && closedGroupOutcome(g) === tab)
		.map((g): DecidedRow => {
			const articleId = g.proposals?.find(
				(p) => p.status === "ACCEPTED" && p.articleId,
			)?.articleId
			return {
				id: g.id,
				title: g.title?.trim() || "Docs suggestion",
				type: "DOCS_SUGGESTION",
				meta: `Docs · ${docsSourceLabel(g.source)}`,
				at: g.closedAt ?? g.createdAt,
				href: articleId ? `/edit-docs/${articleId}` : null,
				reason: null,
			}
		})
	return [...cards, ...docs].sort((a, b) => b.at - a.at)
}
